import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Archive, ArrowLeft, ArrowRight, BookText, CheckCircle2, Clock3,
  Gift, HandCoins, Lock, Pause, Pencil, Play, ShieldAlert,
  UserPlus, Users, XCircle, Zap,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ProgramCard } from '@/features/programs/components/ProgramCard'
import { type ProgramRecord } from '@/types/programs'
import { cn } from '@/lib/utils'

type SectionId = 'concept' | 'configuration' | 'lifecycle' | 'agents' | 'permissions' | 'notifications'

interface DocSection { id: SectionId; title: string }

const DEMO_PROGRAM: ProgramRecord = {
  id: 'demo-program-123',
  business_id: 'biz-1',
  business_name: 'Business Example',
  slug: 'programme-vip-gold',
  name: 'Programme VIP Gold',
  description: 'Programme de parrainage exclusif avec de fortes recompenses par transaction.',
  status: 'active',
  commission_type: 'per-transaction',
  points_per_transaction: 500,
  exchange_mode: 'both',
  points_per_euro: 10,
  eligibility_criteria: 'Reserve aux affilies avec un historique de conversion positif.',
  rule_version: 1,
  starts_at: null, ends_at: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  activated_at: new Date().toISOString(),
  paused_at: null, suspended_at: null, suspension_deadline_at: null,
  exchange_pack: null,
  assigned_agents_count: 5,
  has_open_prospects: true,
  actions: {
    can_create: false, can_update: true, can_edit_general: false,
    can_edit_cash: false, can_edit_rewards: true, can_pause: true,
    can_reactivate: false, can_suspend: false, can_lift_suspension: false,
    can_activate: false, can_archive: false, can_delete_from_archive: false,
    can_soft_delete: false, can_assign_agent: true,
  },
}

const STATUSES = [
  { key: 'draft', label: 'Brouillon', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20', desc: 'Configuration en cours. Aucun agent notifie.', icon: Pencil },
  { key: 'active', label: 'Actif', color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20', desc: 'Operationnel. Les agents peuvent soumettre des prospects.', icon: Play },
  { key: 'paused', label: 'En pause', color: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20', desc: 'Suspendu temporairement. Aucun nouveau prospect.', icon: Pause },
  { key: 'suspended', label: 'Suspendu', color: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20', desc: 'Fermeture controlee en cours. Delai de 30 jours avant archivage.', icon: Clock3 },
  { key: 'archived', label: 'Archive', color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20', desc: 'Lecture seule, definitif. Suppression possible.', icon: Archive },
] as const

interface Transition {
  from: string; to: string; label: string
  condition: string; blocking?: string; notifies?: string
  tone: 'success' | 'warning' | 'danger' | 'neutral'
}

const TRANSITIONS: Transition[] = [
  { from: 'Brouillon', to: 'Actif', label: 'Activation', condition: 'Le programme doit avoir une configuration complete.', blocking: 'Impossible si des champs obligatoires sont manquants.', notifies: 'Email + notification aux affilies actifs deja assignes.', tone: 'success' },
  { from: 'Actif', to: 'En pause', label: 'Pause', condition: 'Proprietaire clique sur Mettre en pause.', notifies: 'Email + notification a tous les affilies assignes.', tone: 'warning' },
  { from: 'En pause', to: 'Actif', label: 'Reactivation', condition: 'Proprietaire clique sur Reactiver.', blocking: 'Bloque si le type de commission est palier de revenus.', notifies: 'Notification de reprise envoyee aux affilies.', tone: 'success' },
  { from: 'Actif', to: 'Suspendu', label: 'Suspension', condition: 'Proprietaire clique sur Suspendre.', blocking: 'Bloque si au moins un prospect est encore ouvert.', notifies: 'Email + notification a tous les affilies assignes.', tone: 'danger' },
  { from: 'En pause', to: 'Suspendu', label: 'Suspension', condition: 'Proprietaire clique sur Suspendre.', blocking: 'Bloque si au moins un prospect est encore ouvert.', notifies: 'Email + notification a tous les affilies assignes.', tone: 'danger' },
  { from: 'Suspendu', to: 'Actif', label: 'Lever la suspension', condition: 'Tant que le programme nest pas archive.', notifies: 'Notification de reprise envoyee aux affilies.', tone: 'success' },
  { from: 'Suspendu', to: 'Archive', label: 'Archivage', condition: 'Le delai de 30 jours apres suspension est depasse.', blocking: 'Impossible avant le deadline de 30 jours.', tone: 'neutral' },
]

interface PermRow {
  action: string; draft: string; active: string
  paused: string; suspended: string; archived: string
}

const PERMISSION_MATRIX: PermRow[] = [
  { action: 'Modifier general', draft: 'Oui', active: 'Non*', paused: 'Non*', suspended: 'Non*', archived: 'Non' },
  { action: 'Modifier cash', draft: 'Oui', active: 'Non*', paused: 'Non*', suspended: 'Non*', archived: 'Non' },
  { action: 'Modifier rewards', draft: 'Oui', active: 'Oui', paused: 'Oui', suspended: 'Non', archived: 'Non' },
  { action: 'Activer', draft: 'Oui', active: 'Non', paused: 'Non', suspended: 'Non', archived: 'Non' },
  { action: 'Pause / Reactiver', draft: 'Non', active: 'Oui', paused: 'Oui**', suspended: 'Non', archived: 'Non' },
  { action: 'Suspendre', draft: 'Non', active: 'Oui***', paused: 'Oui***', suspended: 'Non', archived: 'Non' },
  { action: 'Lever suspension', draft: 'Non', active: 'Non', paused: 'Non', suspended: 'Oui', archived: 'Non' },
  { action: 'Archiver', draft: 'Non', active: 'Non', paused: 'Non', suspended: 'Oui****', archived: 'Non' },
  { action: 'Assigner agents', draft: 'Oui', active: 'Oui', paused: 'Oui', suspended: 'Non', archived: 'Non' },
  { action: 'Supprimer', draft: 'Oui*****', active: 'Non', paused: 'Non', suspended: 'Non', archived: 'Oui' },
  { action: 'Soumettre prospect', draft: 'Non', active: 'Oui', paused: 'Non', suspended: 'Non', archived: 'Non' },
]

const PERM_FOOTNOTES = [
  '* Verrouille des quun agent actif ou un prospect existe.',
  '** La reactivation est bloquee si le type de commission est palier de revenus.',
  '*** Bloque si des prospects ouverts existent.',
  '**** Uniquement apres 30 jours de suspension.',
  '***** Uniquement si aucune assignation ni prospect (jamais exploite).',
]

function LifecycleTimeline() {
  return (
    <div className="relative">
      {/* Vertical connecting line */}
      <div className="absolute left-[15px] top-3 bottom-3 w-px bg-border sm:left-[19px]" />

      <div className="space-y-0">
        {STATUSES.map((s, i) => {
          return (
            <div key={s.key} className="relative flex items-start gap-3 py-2.5">
              {/* Step dot */}
              <div className={cn(
                'relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border-2 bg-background sm:size-10',
                s.color,
              )}>
                <s.icon className="size-3.5 sm:size-4" />
              </div>

              {/* Content */}
              <div className={cn(
                'min-w-0 flex-1 rounded-lg border px-3 py-2.5 sm:px-4',
                s.color,
              )}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{s.label}</span>
                  <span className="text-[10px] font-medium opacity-60">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <p className="mt-0.5 text-xs leading-4 opacity-80">{s.desc}</p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TransitionCard({ transition }: { transition: Transition }) {
  const toneBorder =
    transition.tone === 'success' ? 'border-emerald-500/20 bg-emerald-500/5'
    : transition.tone === 'warning' ? 'border-amber-500/20 bg-amber-500/5'
    : transition.tone === 'danger' ? 'border-rose-500/20 bg-rose-500/5'
    : 'border-border bg-muted/20'

  const toneBadge =
    transition.tone === 'success' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    : transition.tone === 'warning' ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
    : transition.tone === 'danger' ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
    : 'bg-muted text-muted-foreground'

  return (
    <div className={cn('rounded-lg border p-4', toneBorder)}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={cn('text-[10px] uppercase tracking-wide', toneBadge)}>
          {transition.label}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {transition.from} <ArrowRight className="mx-0.5 inline size-3" /> {transition.to}
        </span>
      </div>
      <p className="mt-2 text-sm text-foreground">{transition.condition}</p>
      {transition.blocking && (
        <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
          <XCircle className="mt-0.5 size-3.5 shrink-0 text-rose-500/70" />
          <span>{transition.blocking}</span>
        </div>
      )}
      {transition.notifies && (
        <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
          <Zap className="mt-0.5 size-3.5 shrink-0 text-amber-500/70" />
          <span>{transition.notifies}</span>
        </div>
      )}
    </div>
  )
}

function RuleCard({
  title, children, tone = 'default',
}: {
  title: string; children: React.ReactNode
  tone?: 'default' | 'success' | 'warning' | 'danger'
}) {
  const border =
    tone === 'success' ? 'border-emerald-500/20'
    : tone === 'warning' ? 'border-amber-500/20'
    : tone === 'danger' ? 'border-rose-500/20'
    : 'border-border/60'

  const dot =
    tone === 'success' ? 'bg-emerald-400'
    : tone === 'warning' ? 'bg-amber-400'
    : tone === 'danger' ? 'bg-rose-400'
    : 'bg-muted-foreground/30'

  return (
    <div className={cn('rounded-lg border bg-card px-4 py-3', border)}>
      <div className="flex items-center gap-2">
        <span className={cn('size-1.5 rounded-full', dot)} />
        <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      </div>
      <div className="mt-1.5 pl-3.5 text-sm leading-6 text-muted-foreground">{children}</div>
    </div>
  )
}

function KeyPoint({ icon: Icon, children }: { icon: typeof Zap; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="mt-0.5 size-4 shrink-0 text-primary/80" />
      <span className="text-sm text-muted-foreground">{children}</span>
    </div>
  )
}

const docSections: DocSection[] = [
  { id: 'concept', title: 'Concept' },
  { id: 'configuration', title: 'Configuration' },
  { id: 'lifecycle', title: 'Cycle de vie' },
  { id: 'agents', title: 'Agents' },
  { id: 'permissions', title: 'Permissions' },
  { id: 'notifications', title: 'Notifications' },
]

export function ProgramDocsPage() {
  const [activeSection, setActiveSection] = useState<SectionId>('concept')
  const sectionIds = useMemo(() => docSections.map((s) => s.id), [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible.length > 0) setActiveSection(visible[0]!.target.id as SectionId)
      },
      { rootMargin: '-15% 0px -60% 0px', threshold: [0.1, 0.25, 0.5] },
    )
    sectionIds.forEach((id) => {
      const node = document.getElementById(id)
      if (node) observer.observe(node)
    })
    return () => observer.disconnect()
  }, [sectionIds])

  const scrollToSection = (id: SectionId) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setActiveSection(id)
  }

  return (
    <section className="app-section">
      <div className="mb-6 flex items-center gap-3">
        <Button asChild variant="ghost" size="icon" className="size-8">
          <Link to="/programs" aria-label="Retour aux programmes">
            <ArrowLeft className="size-4" aria-hidden />
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <BookText className="size-4 text-muted-foreground" />
          <h1 className="text-base font-semibold text-foreground">Documentation - Programmes</h1>
        </div>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        <Badge variant="outline" className="gap-1.5 rounded-full px-3 py-1 text-xs">
          <ShieldAlert className="size-3" />
          Regles backend prioritaires
        </Badge>
        <Badge variant="outline" className="gap-1.5 rounded-full px-3 py-1 text-xs">
          <Clock3 className="size-3" />
          Archivage apres 30 jours
        </Badge>
        <Badge variant="outline" className="gap-1.5 rounded-full px-3 py-1 text-xs">
          <Users className="size-3" />
          Gestion des agents
        </Badge>
      </div>

      <div className="grid gap-10 xl:grid-cols-[minmax(0,1fr)_220px]">
        <div className="min-w-0 max-w-3xl space-y-12">
          {/* CONCEPT */}
          <div id="concept" className="scroll-mt-24 space-y-5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/50">01</span>
              <h2 className="text-base font-semibold text-foreground">Concept</h2>
            </div>
            <p className="text-sm text-muted-foreground leading-6">
              Un <strong>programme</strong> est votre contrat commercial central. Il definit la
              commission, les regles dechange et les affilies autorises a promouvoir votre business.
              Le backend recalcule toujours les droits reels avant chaque action.
            </p>

            <div className="pointer-events-none select-none opacity-90">
              <ProgramCard program={DEMO_PROGRAM} mode="owner" />
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {STATUSES.map((s) => (
                <div key={s.key} className={cn('flex flex-col gap-1.5 rounded-lg border px-3 py-3', s.color)}>
                  <div className="flex items-center gap-1.5">
                    <s.icon className="size-3.5" />
                    <span className="text-xs font-bold">{s.label}</span>
                  </div>
                  <span className="text-[11px] leading-4 opacity-80">{s.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CONFIGURATION */}
          <div id="configuration" className="scroll-mt-24 space-y-5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/50">02</span>
              <h2 className="text-base font-semibold text-foreground">Configuration</h2>
            </div>

            <p className="text-sm text-muted-foreground leading-6">
              La creation dun programme se fait en 3 etapes. Chaque etape valide les champs
              obligatoires avant de passer a la suivante.
            </p>

            <Tabs defaultValue="step1" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="step1">Etape 1 - Info</TabsTrigger>
                <TabsTrigger value="step2">Etape 2 - Commission</TabsTrigger>
                <TabsTrigger value="step3">Etape 3 - Apercu</TabsTrigger>
              </TabsList>
              <TabsContent value="step1" className="mt-4 space-y-3">
                <RuleCard title="Nom du programme" tone="default">
                  Obligatoire. Identifie le programme dans toute linterface.
                </RuleCard>
                <RuleCard title="Description" tone="default">
                  Obligatoire. Explique loffre aux agents et prospects.
                </RuleCard>
                <RuleCard title="Criteres deligibilite" tone="default">
                  Obligatoire. Definit qui peut participer.
                </RuleCard>
                <RuleCard title="Statut initial" tone="default">
                  Brouillon, Actif ou En pause. Recommande: Brouillon pour configurer avant publication.
                </RuleCard>
                <RuleCard title="Prefill IACRM (optionnel)" tone="success">
                  Si lAPI IACRM est configuree, vous pouvez pre-remplir depuis un service existant.
                </RuleCard>
              </TabsContent>
              <TabsContent value="step2" className="mt-4 space-y-3">
                <RuleCard title="Type de commission" tone="default">
                  <strong>Par transaction</strong>: points fixes par transaction.<br />
                  <strong>Palier de revenus</strong>: points selon seuils de revenus.
                </RuleCard>
                <RuleCard title="Points par transaction" tone="warning">
                  Obligatoire uniquement si le type est Par transaction. Inutile pour Palier de revenus.
                </RuleCard>
                <RuleCard title="Mode dechange" tone="default">
                  <span className="inline-flex items-center gap-1"><HandCoins className="size-3 text-emerald-500" /><strong>Cash</strong></span> = conversion en euros.<br />
                  <span className="inline-flex items-center gap-1"><Gift className="size-3 text-amber-500" /><strong>Rewards</strong></span> = echange contre des cadeaux.<br />
                  <span className="inline-flex items-center gap-1"><CheckCircle2 className="size-3 text-blue-500" /><strong>Mixte</strong></span> = les deux options disponibles.
                </RuleCard>
                <RuleCard title="Taux cash (points/euro)" tone="warning">
                  Obligatoire si le mode inclut Cash. Definit combien de points valent 1 euro.
                </RuleCard>
                <RuleCard title="Pack rewards" tone="warning">
                  Obligatoire si le mode inclut Rewards. Le pack doit contenir au moins un article actif.
                </RuleCard>
              </TabsContent>
              <TabsContent value="step3" className="mt-4 space-y-3">
                <RuleCard title="Recapitulatif" tone="success">
                  Verifiez toutes les informations avant de creer le programme. Une fois cree, certaines
                  regles seront verrouillees si des agents ou prospects sont associes.
                </RuleCard>
              </TabsContent>
            </Tabs>

            <Separator className="my-4" />

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Regles de modification</h3>
              <RuleCard title="Parametres generaux et cash" tone="warning">
                <strong>Verrouilles</strong> des quun agent actif ou un prospect existe sur le
                programme. Seul le pack rewards reste modifiable apres usage.
              </RuleCard>
              <RuleCard title="Pack rewards" tone="success">
                <strong>Toujours modifiable</strong>, meme apres usage. Chaque modification incremente
                automatiquement la version des regles.
              </RuleCard>
            </div>
          </div>

          {/* LIFECYCLE */}
          <div id="lifecycle" className="scroll-mt-24 space-y-5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/50">03</span>
              <h2 className="text-base font-semibold text-foreground">Cycle de vie</h2>
            </div>

            <p className="text-sm text-muted-foreground leading-6">
              Un programme traverse 5 statuts dans un ordre precis. Chaque transition declenche des
              verifications et, souvent, des notifications.
            </p>

            <LifecycleTimeline />

            <Separator className="my-4" />

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Transitions detaillees</h3>
              {TRANSITIONS.map((t, i) => (
                <TransitionCard key={i} transition={t} />
              ))}
            </div>

            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
              <div className="flex items-start gap-3">
                <Clock3 className="mt-0.5 size-4 shrink-0 text-amber-600" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-800 dark:text-amber-300">Chemin recommande</p>
                  <p className="mt-1 text-amber-700 dark:text-amber-300/80">
                    Actif --&gt; Suspendre --&gt; Attendre 30 jours --&gt; Archiver --&gt; Supprimer
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* AGENTS */}
          <div id="agents" className="scroll-mt-24 space-y-5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/50">04</span>
              <h2 className="text-base font-semibold text-foreground">Gestion des agents</h2>
            </div>

            <div className="space-y-3">
              <RuleCard title="Assigner un affilie" tone="success">
                Permission <strong>assign-agent</strong> requise. Cree une notification in-app.
                Envoie un email si le programme nest pas en brouillon.
              </RuleCard>
              <RuleCard title="Retirer un affilie" tone="warning">
                <strong>Impossible</strong> si l&apos;affilie a deja cree au moins un prospect sur ce
                programme. L&apos;agent est alors verrouille.
              </RuleCard>
              <RuleCard title="Reassigner un affilie retire" tone="success">
                La relation est reactivee avec une <strong>nouvelle date</strong> d&apos;assignation.
              </RuleCard>
              <RuleCard title="Soumission de prospects" tone="default">
                Un agent peut soumettre des prospects uniquement si :<br />
                1. Il a la permission <strong>prospect.submit</strong><br />
                2. Le programme est au statut <strong>Actif</strong>
              </RuleCard>
            </div>

            <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 px-4 py-3">
              <div className="flex items-start gap-3">
                <Lock className="mt-0.5 size-4 shrink-0 text-rose-500" />
                <div className="text-sm">
                  <p className="font-semibold text-rose-800 dark:text-rose-300">Agent verrouille</p>
                  <p className="mt-1 text-rose-700 dark:text-rose-300/80">
                    Un affilie avec des prospects sur un programme ne peut pas en etre retire. Cela
                    garantit la tracabilite des conversions.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* PERMISSIONS */}
          <div id="permissions" className="scroll-mt-24 space-y-5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/50">05</span>
              <h2 className="text-base font-semibold text-foreground">Matrice des permissions</h2>
            </div>

            <p className="text-sm text-muted-foreground leading-6">
              Ce tableau resume les actions possibles selon le statut du programme. Les asterisques
              renvoient aux notes en bas de tableau.
            </p>

            <div className="overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Action</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Brouillon</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Actif</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Pause</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Suspendu</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground">Archive</th>
                  </tr>
                </thead>
                <tbody>
                  {PERMISSION_MATRIX.map((row, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="px-3 py-2 font-medium text-foreground">{row.action}</td>
                      <td className="px-3 py-2 text-center text-muted-foreground">{row.draft}</td>
                      <td className="px-3 py-2 text-center text-muted-foreground">{row.active}</td>
                      <td className="px-3 py-2 text-center text-muted-foreground">{row.paused}</td>
                      <td className="px-3 py-2 text-center text-muted-foreground">{row.suspended}</td>
                      <td className="px-3 py-2 text-center text-muted-foreground">{row.archived}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-1.5">
              {PERM_FOOTNOTES.map((note, i) => (
                <p key={i} className="text-xs text-muted-foreground">{note}</p>
              ))}
            </div>
          </div>

          {/* NOTIFICATIONS */}
          <div id="notifications" className="scroll-mt-24 space-y-5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground/50">06</span>
              <h2 className="text-base font-semibold text-foreground">Notifications automatiques</h2>
            </div>

            <div className="space-y-3">
              <RuleCard title="Activation d&apos;un brouillon" tone="success">
                Email + notification aux <strong>affilies actifs deja assignes</strong>.
              </RuleCard>
              <RuleCard title="Pause / Suspension" tone="warning">
                Email + notification a <strong>tous les affilies assignes</strong>.
              </RuleCard>
              <RuleCard title="Reactivation" tone="success">
                Notification de reprise envoyee aux affilies assignes.
              </RuleCard>
              <RuleCard title="Changement de pack rewards" tone="success">
                Notification de mise a jour rewards envoyee aux affilies assignes.
              </RuleCard>
              <RuleCard title="Assignation / Desassignation" tone="default">
                Notification in-app et email cible a chaque affilie concerne.
              </RuleCard>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="hidden xl:block">
          <div className="sticky top-24 space-y-4">
            <Card className="shadow-none border-border/40">
              <CardHeader className="py-3 px-4 border-b border-border/30">
                <CardTitle className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
                  Sur cette page
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <nav className="flex flex-col">
                  {docSections.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        'w-full rounded-md px-3 py-2 text-left text-xs font-medium transition-colors',
                        activeSection === section.id
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                      )}
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>

            <Card className="shadow-none border-border/40 bg-muted/10">
              <CardContent className="p-4 space-y-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">A retenir</p>
                <div className="space-y-2.5 text-xs text-muted-foreground">
                  <KeyPoint icon={Zap}>Le pack rewards reste flexible apres usage.</KeyPoint>
                  <KeyPoint icon={UserPlus}>Un affilie avec prospects est verrouille au programme.</KeyPoint>
                  <KeyPoint icon={CheckCircle2}>Suspension impossible avec des prospects ouverts.</KeyPoint>
                  <KeyPoint icon={Archive}>30 jours de suspension avant archivage.</KeyPoint>
                </div>
              </CardContent>
            </Card>
          </div>
        </aside>
      </div>
    </section>
  )
}
